/**
 * _compareBlocks.jsx : sous-blocs visuels de CompareSessionsPage (convention
 * D52 : fichier privé au dossier, composants sans état, sans logique métier).
 * Extraits en 10.5.297 pour garder la page sous 300 lignes avec la plage
 * d'analyse ; partagés par CompareSessionsView (D257).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { getTheme } from '../constants/theme';
import { formatDate, formatDateLocale, formatNumberLocale } from '../utils/formatters';
import { sensorLabel } from '../constants/sensorLabels';
import { durationWithRange, sessionFullDuration } from './analysisRangeText';
import { ALL_FIELD, anySessionMeasures } from './compareMetrics';

/** Thème de la comparaison : t (getTheme), couleurs c et style des cartes. */
export function compareTheme(darkMode) {
  const t = getTheme(darkMode);
  const c = { bg: t.bg, card: t.card, text: t.textPrimary, textSec: t.textSecondary, accent: t.accent, border: t.border };
  const card = { background: c.card, borderRadius: '12px', border: `1px solid ${c.border}`, padding: '20px', marginBottom: '16px' };
  return { t, c, card };
}

/** Retour à la liste des sessions. */
export function BackButton({ onBack, c }) {
  const { t: tr } = useTranslation();
  return (
    <button onClick={onBack} style={{ padding: '8px 16px', borderRadius: '8px', background: 'transparent',
      border: `1px solid ${c.border}`, color: c.text, cursor: 'pointer',
      fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <ChevronLeft size={14} strokeWidth={2.5} aria-hidden="true" />
      {tr('sessions.compare.back')}
    </button>
  );
}

/**
 * Légende des sessions (couleur, titre, date, capteur, mesures chargées), survol
 * mis en évidence. « Non mesuré » sur la carte d'une session sans la mesure de
 * l'onglet actif (field), jamais sur l'onglet « Tous les paramètres ».
 */
export function SessionLegend({ loaded, palette, highlight, setHighlight, card, c, t, darkMode, field }) {
  const { t: tr } = useTranslation();
  return (
    <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
      {loaded.map((s, i) => (
        <div key={s.session?._id || i}
          onMouseEnter={() => setHighlight(i)} onMouseLeave={() => setHighlight(null)}
          style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '10px 14px', borderRadius: '10px',
            background: highlight === i ? (darkMode ? '#2D2D50' : '#F0F7FF') : t.card,
            border: `1.5px solid ${highlight === i ? palette.styleAt(i).color : c.border}`,
            transition: 'all 0.15s', flex: '1 1 220px', minWidth: 0,
          }}>
          <div style={{ width: '14px', height: '14px', borderRadius: '50%',
            flexShrink: 0, background: palette.styleAt(i).color }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: c.text,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {s.session?.title || tr('sessions.compare.fallbackName', { n: i + 1 })}
            </div>
            <div style={{ fontSize: '11px', color: c.textSec }}>
              {s.session?.start_time
                ? formatDateLocale(s.session.start_time,
                    { day: '2-digit', month: 'short', year: 'numeric' })
                : tr('common.notProvided')}
              {' · '}{sensorLabel(s.session, tr)}
              {' · '}{tr('sessions.compare.measurements', { count: s.readings.length })}
              {field && field !== ALL_FIELD && !anySessionMeasures([s], field) && (
                <span data-testid="legend-not-measured">{' · '}{tr('common.notMeasured')}</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Légende des lignes de seuil du graphique (libellé et unité de la mesure). */
export function ThresholdLegend({ thresholds, c }) {
  const { t: tr } = useTranslation();
  if (!thresholds.length) return null;
  return (
    <div style={{ display: 'flex', gap: '16px', marginTop: '10px', flexWrap: 'wrap' }}>
      {thresholds.map(th => (
        <span key={th.label} style={{ display: 'flex', alignItems: 'center', gap: '5px',
          fontSize: '11px', color: c.textSec }}>
          <span style={{ width: '18px', height: '0', borderTop: `2px dashed ${th.color}`,
            display: 'inline-block' }} />
          {tr('sessions.compare.thresholdLegend', { label: th.label, unit: th.unit })}
        </span>
      ))}
    </div>
  );
}

/**
 * Résumé par session : durée de la session entière, suivie de la durée
 * retenue dans la plage quand elle diffère (« 1 j 14 h, dont 23 h 30 dans la plage »).
 */
export function DurationSummary({ loaded, quality, palette, card, c, t }) {
  const { t: tr } = useTranslation();
  return (
    <div style={{ ...card, display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      {loaded.map((s, i) => (
        <div key={i} style={{ flex: '1 1 180px', padding: '14px', borderRadius: '10px',
          background: t.card,
          border: `1.5px solid ${palette.styleAt(i).color}22` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%',
              background: palette.styleAt(i).color }} />
            <span style={{ fontSize: '12px', fontWeight: '700', color: c.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.session?.title || tr('sessions.compare.fallbackName', { n: i + 1 })}
            </span>
          </div>
          {[
            [tr('sessions.compare.summary.date'), s.session?.start_time
              ? formatDate(s.session.start_time, false)
              : tr('common.notProvided')],
            [tr('sessions.compare.summary.duration'),
              durationWithRange(sessionFullDuration(s.session), quality[i], tr)],
            [tr('sessions.compare.summary.type'), sensorLabel(s.session, tr)],
            [tr('sessions.compare.summary.measurements'), formatNumberLocale(s.readings.length, 0)],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px',
              fontSize: '12px', marginBottom: '3px' }}>
              <span style={{ color: c.textSec }}>{k}</span>
              <span style={{ color: c.text, fontWeight: '500', textAlign: 'right' }}>{v}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
